import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import {
  classifySeedStatus,
  getActionApplicability,
} from '@/components/actions';
import { AskMarcusButton } from '@/components/actions/AskMarcusButton';
import { getHardDeleteDisabledReason } from '@/components/actions/constants/hardDelete';
import { HardDeleteTrigger } from '@/components/actions/HardDeleteTrigger';
import {
  buildAnnotateAction,
  buildExportAction,
  buildGenericCustomAction,
  buildImportAction,
  buildManageAction,
  buildScanActionHandlers,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { Button } from '@/components/ui/button';
import { useAxios } from '@/hooks/useAxios';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useSummonAurelius } from '@/hooks/useSummonAurelius';
import { queryClient } from '@/queryclient';
import { getSeedTypeConfig } from '@/sections/seeds/config/seedTypeConfigs';
import { useRefreshSeedData } from '@/sections/seeds/hooks/actions/useRefreshSeedData';
import type { SeedsActionHandlers } from '@/sections/seeds/hooks/actions/useSeedsActionHandlers';
import type { SeedModalHandlers } from '@/sections/seeds/hooks/useSeedsActions';
import {
  isSeedDomain,
  isSeedIP,
  isSeedWebApplication,
  Seed as SeedDiscriminated,
  SeedType,
} from '@/sections/seeds/types';
import { isSeedRefreshable, isSeedScannable } from '@/sections/seeds/utils';
import { useAuth } from '@/state/auth';
import { Seed, SeedStatus } from '@/types';
import { runChunked } from '@/utils/runChunked';

/**
 * Reason shown under the disabled scan items when the selection contains a
 * non-scannable seed (backend scan-status gate: F/FR/D/P/PL → HTTP 400).
 */
const NON_SCANNABLE_REASON =
  'One or more selected seeds are frozen, rejected, deleted, or pending approval';

/**
 * Reason shown under the disabled Refresh Data item when a domain or IP/CIDR
 * member blocks the refresh (F/FR/D; Pending is allowed by the enrichment
 * carve-out).
 */
const NON_REFRESHABLE_REASON =
  'One or more selected domain or IP seeds are frozen, rejected, or deleted';

/**
 * Props for SeedsActions component
 *
 * Refactored to use coordinator pattern - receives handlers from parent
 */
interface SeedsActionsProps {
  /** Current seed type (determines which actions are available) */
  seedType: SeedType;
  /** Selected seeds for bulk actions */
  selectedSeeds: Seed[];
  /** Number of selected rows */
  selectedRowsCount: number;
  /** Modal handlers from coordinator */
  modalHandlers: SeedModalHandlers;
  /** Action handlers from coordinator */
  actionHandlers: SeedsActionHandlers;
  /** Callback to clear selection after actions */
  onClearSelection: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Hide the "Add Seed" button (e.g., in drawer context) */
  hideAddSeed?: boolean;
  /**
   * Callback that opens the credential management modal for a single web app seed.
   * Provided as a direct prop (not on SeedModalHandlers) per F12 resolution.
   */
  onCredentialsClick?: () => void;
  /**
   * Opens the bulk "Delete Permanently" (hard delete) confirmation. When provided
   * (Praetorian-only), a destructive action is added to the toolbar. Bulk hard delete
   * accepts an explicit selection only, capped at 1000 keys.
   */
  onHardDelete?: () => void;
  /** True when the current selection is "select all matching" (query mode). */
  isSelectAll?: boolean;
}

/**
 * Seeds section bulk actions toolbar
 *
 * **Refactored Pattern**: Uses coordinator pattern to receive handlers and modal state from parent.
 * This eliminates code duplication and provides a single source of truth for actions.
 *
 * **Configuration-Driven**: Uses SeedTypeConfig to determine which actions to show
 * - Preseed: Only shows approve/reject actions
 * - Domain/IP/Web App: Shows all actions (comment, tag, scan, share, export, status changes)
 *
 * Displays bulk action buttons using shared dropdown components:
 * - AnnotateDropdown (Comment, Tag, Remove Tag) - shown only if config.actions.comment && config.actions.tag
 * - ShareButton (Share URLs) - shown only if config.actions.share
 * - ExportButton (Export) - always enabled (no config check)
 * - ManagedDropdown (Approve, Reject, Freeze, Delete) - config-driven status changes
 * - ScanDropdown (Various scans) - shown only if config.actions.scan
 *
 * Features:
 * - Share URLs: Copies deep links to clipboard for selected seeds
 * - Export: Opens modal for exporting seeds (modal managed by parent)
 * - Status changes: Approve, reject, freeze, delete operations (config-driven)
 * - Annotations: Add comments and tags (config-driven)
 *
 * **Performance**: memoization is handled automatically by the React Compiler
 *
 * @example
 * ```tsx
 * const { modalHandlers, handlers } = useSeedsActions({...});
 *
 * <SeedsActions
 *   seedType="domain"
 *   selectedSeeds={selectedSeeds}
 *   selectedRowsCount={selectedKeys.length}
 *   modalHandlers={modalHandlers}
 *   actionHandlers={handlers}
 *   onClearSelection={clearSelection}
 * />
 * ```
 */
export function SeedsActions({
  seedType,
  selectedSeeds,
  selectedRowsCount,
  modalHandlers,
  actionHandlers,
  onClearSelection: _onClearSelection,
  className,
  hideLabel,
  hideActions,
  hideAddSeed,
  onCredentialsClick,
  onHardDelete,
  isSelectAll,
}: SeedsActionsProps) {
  const { profiles } = useScanProfiles();
  const { isPraetorianUser } = useAuth();
  const axios = useAxios();
  const navigate = useNavigate();
  const { refresh: refreshSeedData, isRefreshing: isRefreshingSeedData } =
    useRefreshSeedData();

  // Compute selection state for smart-context scan profile menu
  const withProfileCount = selectedSeeds.filter(s => !!s.profile).length;
  const scanProfileSelectionState =
    withProfileCount === 0
      ? ('none' as const)
      : withProfileCount === selectedSeeds.length
        ? ('all' as const)
        : ('mixed' as const);

  // Run PUT requests for each target and return failed keys.
  const runSeedPuts = async (
    targets: Seed[],
    buildBody: (seed: Seed) => Record<string, unknown>
  ): Promise<Set<string>> => {
    const result = await runChunked(
      targets,
      seed => axios.put('/seed', buildBody(seed)),
      { concurrency: 8 }
    );
    return new Set(result.failures.map(f => f.item.key));
  };

  // Add a profile only to seeds that currently have none
  const handleAddProfile = async (profileId: string) => {
    const targets = selectedSeeds.filter(s => !s.profile);
    if (targets.length === 0) return;
    const failedKeys = await runSeedPuts(targets, seed => ({
      key: seed.key,
      profile: profileId,
    }));
    const succeededKeys = new Set(
      targets.filter(t => !failedKeys.has(t.key)).map(t => t.key)
    );
    queryClient.setQueriesData(
      {
        predicate: query => {
          const qk = query.queryKey as string[];
          return qk[0] === 'MY' && qk[1] === 'seed';
        },
      },
      (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const data = old as { pages?: { data: Seed[] }[] };
        if (!data.pages) return old;
        return {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            data: page.data.map((s: Seed) =>
              succeededKeys.has(s.key) ? { ...s, profile: profileId } : s
            ),
          })),
        };
      }
    );
    await queryClient.invalidateQueries({
      predicate: query => {
        const qk = query.queryKey as string[];
        return qk[0] === 'MY' && qk[1] === 'seed';
      },
    });
    if (failedKeys.size > 0) {
      toast.error(
        `Failed to assign scan profile for ${failedKeys.size} seed${failedKeys.size === 1 ? '' : 's'}`
      );
    } else {
      toast.success('Scan profile assigned');
    }
  };

  // Remove profile from seeds that currently have one
  const handleRemoveProfile = async () => {
    const targets = selectedSeeds.filter(s => !!s.profile);
    if (targets.length === 0) return;
    const failedKeys = await runSeedPuts(targets, seed => ({
      key: seed.key,
      profile: '',
    }));
    const succeededKeys = new Set(
      targets.filter(t => !failedKeys.has(t.key)).map(t => t.key)
    );
    queryClient.setQueriesData(
      {
        predicate: query => {
          const qk = query.queryKey as string[];
          return qk[0] === 'MY' && qk[1] === 'seed';
        },
      },
      (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const data = old as { pages?: { data: Seed[] }[] };
        if (!data.pages) return old;
        return {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            data: page.data.map((s: Seed) =>
              succeededKeys.has(s.key) ? { ...s, profile: undefined } : s
            ),
          })),
        };
      }
    );
    await queryClient.invalidateQueries({
      predicate: query => {
        const qk = query.queryKey as string[];
        return qk[0] === 'MY' && qk[1] === 'seed';
      },
    });
    if (failedKeys.size > 0) {
      toast.error(
        `Failed to remove scan profile for ${failedKeys.size} seed${failedKeys.size === 1 ? '' : 's'}`
      );
    } else {
      toast.success('Scan profile removed');
    }
  };

  const handleManageProfiles = () => {
    navigate(`../${AuthenticatedRoute.SETTINGS_SCAN}`);
  };

  // Get configuration for this seed type
  const config = getSeedTypeConfig(seedType);

  // Determine which scan types to enable based on selected seeds
  const hasWebApps = selectedSeeds.some(seed =>
    isSeedWebApplication(seed as SeedDiscriminated)
  );
  const hasDomainOrIP = selectedSeeds.some(
    seed =>
      isSeedDomain(seed as SeedDiscriminated) ||
      isSeedIP(seed as SeedDiscriminated)
  );
  const hasDomain = selectedSeeds.some(seed =>
    isSeedDomain(seed as SeedDiscriminated)
  );
  const hasIP = selectedSeeds.some(seed => isSeedIP(seed as SeedDiscriminated));
  const hasSelection = selectedSeeds.length > 0;

  // Client-side mirror of the backend scan-status gate (a denylist): scans are
  // blocked for F/FR/D/P/PL and unknown statuses fail open (enforcement is
  // server-side), so any non-scannable member disables the scan items. The
  // refresh item is gated only by the seeds that would actually be refreshed —
  // domain and IP/CIDR members (useRefreshSeedData filters by class) — so
  // web-app/other statuses must not gate it. Any F/FR/D domain or IP member
  // disables refresh; Pending (P/PL) stays enabled per the pending-enrichment
  // carve-out.
  //
  // The two gates deliberately differ in SCOPE, not just statuses: the scan
  // gate spans the WHOLE selection (conservative — the scan handlers
  // class-filter before POSTing, so a mixed selection would otherwise succeed
  // partially and silently skip the blocked members), while the refresh gate
  // scopes to exactly the classes refresh submits (domain + IP/CIDR).
  const hasNonScannableSeeds = selectedSeeds.some(s => !isSeedScannable(s));
  const hasNonRefreshableSeeds = selectedSeeds
    .filter(seed => {
      const s = seed as SeedDiscriminated;
      return isSeedDomain(s) || isSeedIP(s);
    })
    .some(s => !isSeedRefreshable(s));

  const { summonAurelius } = useSummonAurelius();

  // Build action configurations (config-driven)
  const actions = [];

  // Ask Marcus action - always first position
  actions.push(
    buildGenericCustomAction({
      render: () => (
        <AskMarcusButton
          onClick={() =>
            summonAurelius({
              type: 'seed',
              keys: selectedSeeds.map(s => s.key),
              displayNames: selectedSeeds.map(s => s.name || s.identifier),
            })
          }
          disabled={selectedRowsCount === 0}
          data-testid="seeds-ask-marcus-button"
        />
      ),
      position: 'first',
      testId: 'seeds-ask-marcus-wrapper',
    })
  );

  // Annotate actions (Comment, Tag, Remove Tag, Scan Profile) - only if config allows
  if (config.actions.comment && config.actions.tag) {
    actions.push(
      buildAnnotateAction({
        onCommentClick: modalHandlers.openCommentModal,
        onTagClick: modalHandlers.openTagModal,
        onRemoveTagClick: modalHandlers.openRemoveTagModal,
        labels: { comment: 'Add Note' },
        scanProfile: {
          profiles,
          selectionState: scanProfileSelectionState,
          onAdd: handleAddProfile,
          onRemove: handleRemoveProfile,
          onManage: handleManageProfiles,
        },
        testId: 'seeds-annotate-dropdown',
      })
    );
  }

  // State-aware gating: hide the Freeze/Unfreeze no-op, hide Approve on a
  // fully-frozen selection (it duplicates Unfreeze — same Accept mutation),
  // and disable the current-state status verb with an inline reason. A single
  // util drives all three surfaces (seeds/assets bulk + asset row menu); mixed
  // selections stay permissive and select-all-matching short-circuits to
  // permissive.
  const applicability = getActionApplicability(
    selectedSeeds.map(s => classifySeedStatus(s.status)),
    !!isSelectAll
  );

  // Manage actions (Approve, Reject, Freeze, Delete) - config-driven
  const statusOptions = [];
  if (config.actions.approve && !applicability.hideApprove) {
    statusOptions.push({
      label: 'Approve',
      value: SeedStatus.Accept,
      icon: <CheckCircle className="size-4" />,
      shortcut: '⌥ A',
      disabled: !!applicability.approveDisabledReason,
      description: applicability.approveDisabledReason ?? undefined,
    });
  }
  if (config.actions.reject) {
    statusOptions.push({
      label: 'Reject',
      value: SeedStatus.Reject,
      icon: <XCircle className="size-4" />,
      shortcut: '⌥ R',
      disabled: !!applicability.rejectDisabledReason,
      description: applicability.rejectDisabledReason ?? undefined,
    });
  }

  // Only add manage action if we have status options OR freeze/delete
  if (
    statusOptions.length > 0 ||
    config.actions.freeze ||
    config.actions.delete
  ) {
    // No-op handler for disabled actions
    const noOp = () => {};

    actions.push(
      buildManageAction({
        onFreezeClick: config.actions.freeze
          ? () => void actionHandlers.handleFreeze()
          : noOp,
        onActivateClick: config.actions.approve
          ? () => void actionHandlers.handleActivate()
          : noOp,
        onDeleteClick: config.actions.delete
          ? () => void actionHandlers.handleDelete()
          : noOp,
        onCredentialsClick,
        statusOptions,
        onStatusChangeClick: async status => {
          switch (status as SeedStatus) {
            case SeedStatus.Accept:
              await actionHandlers.handleApprove();
              break;
            case SeedStatus.Reject:
              await actionHandlers.handleReject();
              break;
          }
        },
        labels: { activate: 'Unfreeze' },
        shortcuts: config.actions.freeze
          ? { freeze: ' ⌥ F', delete: ' ⌥ D' }
          : { delete: ' ⌥ D' },
        // Hide the Freeze/Unfreeze no-op by state. Also honor the seed-type
        // config: seed types without a freeze action (e.g. preseed) keep both
        // items off entirely so state gating never surfaces a noop verb.
        hideActions: {
          freeze: !config.actions.freeze || applicability.hideFreeze,
          activate: !config.actions.freeze || applicability.hideUnfreeze,
        },
        testId: 'seeds-managed-dropdown',
      })
    );
  }

  // Delete Permanently (hard delete) - Praetorian-only, additive alongside the
  // soft Delete in the Manage dropdown. Bulk hard delete goes through the
  // 'delete' bulk action; the backend accepts an explicit selection only,
  // capped at MAX_HARD_DELETE_KEYS. The control stays VISIBLE but becomes
  // DISABLED (with a keyboard/AT-reachable reason) for "select all matching"
  // and for selections over the cap — reason copy is centralized in
  // getHardDeleteDisabledReason so it can't drift across entities.
  if (onHardDelete && isPraetorianUser && config.actions.delete) {
    actions.push(
      buildGenericCustomAction({
        render: () => (
          <HardDeleteTrigger
            disabled={selectedRowsCount === 0}
            disabledReason={getHardDeleteDisabledReason({
              isSelectAll: !!isSelectAll,
              selectedCount: selectedRowsCount,
            })}
            onClick={onHardDelete}
            data-testid="seeds-delete-permanently-button"
          />
        ),
        testId: 'seeds-delete-permanently-wrapper',
      })
    );
  }

  // Scan actions - only if config allows
  if (config.actions.scan) {
    actions.push(
      buildScanActionHandlers({
        handlers: {
          onComprehensiveScan: () => actionHandlers.handleComprehensiveScan(),
          onNetworkScan: () => actionHandlers.handleNetworkScan(),
          onPortScan: () => actionHandlers.handlePortScan(),
          onCrawlScan: () => actionHandlers.handleWebCrawl(),
          onWebScan: () => actionHandlers.handleWebScan(),
          // Data refresh (re-run WHOIS for domains, ip capability for IP/CIDR).
          // Omitted when no domain or IP/CIDR seed is selected so the dropdown
          // item doesn't render.
          ...(hasDomain || hasIP
            ? {
                onRefreshData: () => {
                  if (isRefreshingSeedData) return;
                  void refreshSeedData(
                    selectedSeeds as unknown as SeedDiscriminated[]
                  );
                },
              }
            : {}),
        },
        disabledActions: {
          comprehensive: hasNonScannableSeeds,
          network: !hasDomainOrIP || hasNonScannableSeeds,
          port: !hasDomainOrIP || hasNonScannableSeeds,
          crawl: !hasWebApps || hasNonScannableSeeds,
          webscan: !hasWebApps || hasNonScannableSeeds,
          refreshData: isRefreshingSeedData || hasNonRefreshableSeeds,
        },
        // Accessible reason line under the disabled items — only set when the
        // STATUS gate is the cause (class-mismatch disables carry no reason,
        // matching the pre-existing behavior for those).
        disabledReasons: {
          ...(hasNonScannableSeeds
            ? {
                comprehensive: NON_SCANNABLE_REASON,
                network: NON_SCANNABLE_REASON,
                port: NON_SCANNABLE_REASON,
                crawl: NON_SCANNABLE_REASON,
                webscan: NON_SCANNABLE_REASON,
              }
            : {}),
          ...(hasNonRefreshableSeeds
            ? { refreshData: NON_REFRESHABLE_REASON }
            : {}),
        },
        labels: {
          crawl: 'Web Crawl',
          webscan: 'Web Scan (DAST)',
        },
        disabled: !hasSelection,
        testId: 'seeds-scan-dropdown',
      })
    );
  }

  // Share action - only if config allows
  if (config.actions.share) {
    actions.push(
      buildShareAction({
        variant: 'button-only',
        onShareClick: actionHandlers.handleShare,
        testId: 'seeds-share-button',
      })
    );
  }

  // Add Seed action - shown in table context, hidden in drawer
  // Uses rounded styleType for pill shape, with className override for purple fill
  if (seedType !== 'preseed' && !hideAddSeed) {
    actions.push(
      buildGenericCustomAction({
        render: () => (
          <Button
            onClick={modalHandlers.openAddSeedModal}
            variant="outline"
            size="sm"
            className="bg-chariot-brand-primary border-chariot-brand-primary hover:bg-chariot-brand-secondary rounded-full text-white hover:text-white"
            data-testid="seeds-add-button"
            aria-label="Add Seed"
          >
            <PlusCircleIcon className="size-4" />
            Add Seed
          </Button>
        ),
        testId: 'seeds-add-button',
        position: 'first',
      })
    );
  }

  // Import action - always enabled (no config check)
  actions.push(
    buildImportAction({
      onImportClick: modalHandlers.openImportModal,
      disabled: false, // Always enabled
      testId: 'seeds-import-button',
    })
  );

  // Export action - always enabled (no config check)
  actions.push(
    buildExportAction({
      onExportClick: modalHandlers.openExportModal,
      disabled: selectedRowsCount === 0,
      alwaysEnabled: false,
      testId: 'seeds-export-button',
    })
  );

  return (
    <TableActionsBar
      selectedItems={selectedSeeds}
      actions={actions}
      disabled={selectedRowsCount === 0}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="seeds"
    />
  );
}
